// @ts-nocheck
import { useState, useRef } from "react";
import { useListTemplates, useCreateTemplate, useDeleteTemplate, getListTemplatesQueryKey } from "../lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/layout/PageHeader";
import { StatCard } from "../components/StatCard";
import { useUpload } from "../lib/object-storage-web";
import { useLang } from "../lib/lang";

const CATEGORIES = ["All", "General", "Promotion", "Notification", "Reminder", "Alert"];
const API = import.meta.env.VITE_API_URL ?? "";

function mediaUrl(objectPath: string) {
  return `${API}/api/storage${objectPath}`;
}

type MediaType = "text" | "image" | "video";

export function Templates() {
  const { t } = useLang();
  const tl = t.templates;
  const tc = t.common;
  const qc = useQueryClient();
  const { data: templates, isLoading } = useListTemplates();
  const createTemplate = useCreateTemplate();
  const deleteTemplate = useDeleteTemplate();

  const [showForm, setShowForm] = useState(false);
  const [filterCat, setFilterCat] = useState("All");
  const [search, setSearch] = useState("");
  const [tName, setTName] = useState("");
  const [tContent, setTContent] = useState("");
  const [tCategory, setTCategory] = useState("General");
  const [tType, setTType] = useState<MediaType>("text");
  const [tButtons, setTButtons] = useState<string[]>([]);
  const [uploadedPath, setUploadedPath] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadFile, isUploading, progress } = useUpload({
    basePath: `${API}/api/storage`,
    onSuccess: (res) => {
      setUploadedPath(res.objectPath);
    },
    onError: () => {
      setError(tl.uploadFailed);
    },
  });

  const filtered = (templates ?? []).filter(t =>
    (filterCat === "All" || t.category === filterCat) &&
    (!search || t.name.toLowerCase().includes(search.toLowerCase()) || t.content.toLowerCase().includes(search.toLowerCase()))
  );

  const resetForm = () => {
    setTName(""); setTContent(""); setTCategory("General");
    setTType("text"); setTButtons([]); setUploadedPath(null); setPreviewUrl(null); setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploadedPath(null);
    setPreviewUrl(URL.createObjectURL(file));
    await uploadFile(file);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if ((tType === "image" || tType === "video") && !uploadedPath) {
      setError(tl.uploadRequired.replace("{type}", tType === "image" ? tl.image : tl.video));
      return;
    }
    try {
      await createTemplate.mutateAsync({
        data: {
          name: tName,
          content: tContent,
          category: tCategory,
          type: tType,
          imageUrl: (tType === "image" || tType === "video") ? uploadedPath : null,
          buttons: tButtons.filter(b => b.trim()).length > 0
            ? JSON.stringify(tButtons.filter(b => b.trim()))
            : null,
        },
      } as any);
      resetForm();
      setShowForm(false);
      qc.invalidateQueries({ queryKey: getListTemplatesQueryKey() });
    } catch {
      setError(tl.createFailed);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm(tl.deleteConfirm)) return;
    await deleteTemplate.mutateAsync({ id });
    qc.invalidateQueries({ queryKey: getListTemplatesQueryKey() });
  };

  const handleDuplicate = async (id: number) => {
    await fetch(`${API}/api/templates/${id}/duplicate`, { method: "POST" });
    qc.invalidateQueries({ queryKey: getListTemplatesQueryKey() });
  };

  const catColor = (cat: string) => {
    const map: Record<string, string> = {
      General: "bg-[#eef0fd] text-[#1171ef]",
      Promotion: "bg-[#d4edda] text-[#155724]",
      Notification: "bg-[#cce5ff] text-[#004085]",
      Reminder: "bg-[#fff3cd] text-[#856404]",
      Alert: "bg-[#f8d7da] text-[#721c24]",
    };
    return map[cat] ?? "bg-[#e9ecef] text-[#525f7f]";
  };

  const typeButtons: { key: MediaType; icon: string; labelKey: keyof typeof tl }[] = [
    { key: "text", icon: "fas fa-align-left", labelKey: "textOnly" },
    { key: "image", icon: "fas fa-image", labelKey: "textImage" },
    { key: "video", icon: "fas fa-video", labelKey: "textVideo" },
  ];

  return (
    <div>
      <PageHeader
        title={tl.title}
        actions={[{ label: tl.newTemplate, icon: "fas fa-plus", onClick: () => { resetForm(); setShowForm(true); } }]}
      />

      <div className="px-6 pb-6 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <StatCard label={tl.totalTemplates} value={templates?.length ?? 0} iconClass="fas fa-file-alt" gradient="primary" />
          <StatCard label={`${tl.image}`} value={templates?.filter(tp => tp.type === "image").length ?? 0} iconClass="fas fa-image" gradient="success" />
          <StatCard label={`${tl.video}`} value={templates?.filter(tp => tp.type === "video").length ?? 0} iconClass="fas fa-video" gradient="info" />
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9ecef] sticky top-0 bg-white z-10">
                <h5 className="font-semibold text-[#32325d]">{tl.newTemplate}</h5>
                <button onClick={() => { setShowForm(false); resetForm(); }} className="text-[#8898aa] hover:text-[#32325d]">
                  <i className="fas fa-times" />
                </button>
              </div>
              <form onSubmit={handleCreate} className="p-6 space-y-4">
                {error && (
                  <div className="bg-[#fff3cd] border border-[#ffc107] text-[#856404] px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <i className="fas fa-exclamation-triangle" />
                    {error}
                  </div>
                )}

                {/* Type toggle */}
                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-2">{tl.mediaType}</label>
                  <div className="flex rounded-lg border border-[#e9ecef] overflow-hidden">
                    {typeButtons.map((btn, i) => (
                      <button
                        key={btn.key}
                        type="button"
                        onClick={() => { setTType(btn.key); setUploadedPath(null); setPreviewUrl(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                        className={`flex-1 py-2 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                          i > 0 ? "border-l border-[#e9ecef]" : ""
                        } ${tType === btn.key ? "gradient-primary text-white" : "text-[#525f7f] hover:bg-[#f6f9fc]"}`}
                      >
                        <i className={btn.icon} />
                        {tl[btn.labelKey] as string}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{tl.templateName}</label>
                  <input
                    value={tName}
                    onChange={e => setTName(e.target.value)}
                    className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                    placeholder={tl.namePlaceholder}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{tl.category}</label>
                  <select
                    value={tCategory}
                    onChange={e => setTCategory(e.target.value)}
                    className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]"
                  >
                    {CATEGORIES.filter(c => c !== "All").map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>

                {/* Image upload */}
                {tType === "image" && (
                  <div>
                    <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{tl.image}</label>
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg overflow-hidden cursor-pointer transition-colors ${
                        isUploading ? "border-[#1171ef] bg-[#eef0fd]" : "border-[#cad1d7] hover:border-[#1171ef]"
                      }`}
                    >
                      {previewUrl ? (
                        <div className="relative">
                          <img src={previewUrl} alt="Preview" className="w-full h-40 object-cover" />
                          <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${isUploading ? "opacity-100 bg-black/40" : "opacity-0 hover:opacity-100 bg-black/30"}`}>
                            {isUploading ? (
                              <div className="text-center text-white">
                                <i className="fas fa-spinner fa-spin text-xl mb-1 block" />
                                <span className="text-xs">{tl.uploading.replace("{pct}", String(progress))}</span>
                              </div>
                            ) : (
                              <div className="text-center text-white">
                                <i className="fas fa-edit text-xl mb-1 block" />
                                <span className="text-xs">{tl.clickToChange}</span>
                              </div>
                            )}
                          </div>
                          {uploadedPath && (
                            <div className="absolute bottom-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                              <i className="fas fa-check" /> {tl.uploaded}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="p-8 text-center">
                          <i className="fas fa-cloud-upload-alt text-3xl text-[#8898aa] mb-2 block" />
                          <p className="text-sm text-[#525f7f] font-medium">{tl.uploadImage}</p>
                          <p className="text-xs text-[#8898aa] mt-1">{tl.imageUploadHint}</p>
                        </div>
                      )}
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
                  </div>
                )}

                {/* Video upload */}
                {tType === "video" && (
                  <div>
                    <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{tl.video}</label>
                    <div
                      onClick={() => !isUploading && fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg overflow-hidden cursor-pointer transition-colors ${
                        isUploading ? "border-[#1171ef] bg-[#eef0fd] cursor-not-allowed" : "border-[#cad1d7] hover:border-[#1171ef]"
                      }`}
                    >
                      {previewUrl ? (
                        <div className="relative">
                          <video
                            src={previewUrl}
                            className="w-full h-40 object-cover"
                            controls={false}
                            muted
                            playsInline
                          />
                          <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${isUploading ? "opacity-100 bg-black/40" : "opacity-0 hover:opacity-100 bg-black/30"}`}>
                            {isUploading ? (
                              <div className="text-center text-white">
                                <i className="fas fa-spinner fa-spin text-xl mb-1 block" />
                                <span className="text-xs">{tl.uploading.replace("{pct}", String(progress))}</span>
                              </div>
                            ) : (
                              <div className="text-center text-white">
                                <i className="fas fa-play-circle text-3xl mb-1 block" />
                                <span className="text-xs">{tl.clickToChange}</span>
                              </div>
                            )}
                          </div>
                          {uploadedPath && (
                            <div className="absolute bottom-2 right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                              <i className="fas fa-check" /> {tl.uploaded}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="p-8 text-center">
                          <i className="fas fa-video text-3xl text-[#8898aa] mb-2 block" />
                          <p className="text-sm text-[#525f7f] font-medium">{tl.uploadVideo}</p>
                          <p className="text-xs text-[#8898aa] mt-1">{tl.videoUploadHint}</p>
                        </div>
                      )}
                    </div>
                    <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleFileChange} />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{tl.content}</label>
                  <textarea
                    value={tContent}
                    onChange={e => setTContent(e.target.value)}
                    rows={4}
                    className="w-full border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef] resize-none"
                    placeholder="Hello {{name}}, ..."
                    required
                  />
                  <p className="text-xs text-[#8898aa] mt-1">{tl.personalizationHint}</p>
                </div>

                {/* Buttons section */}
                <div className="border border-[#e9ecef] rounded-xl p-4 space-y-3 bg-[#f8f9fe]">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-[#32325d]">
                        <i className="fas fa-th-large mr-1.5 text-[#1171ef]" />
                        {tl.buttons} <span className="text-[#8898aa] font-normal">({tc.optional})</span>
                      </p>
                      <p className="text-[11px] text-[#8898aa] mt-0.5">{tl.buttonsHint}</p>
                    </div>
                    {tButtons.length < 3 && (
                      <button
                        type="button"
                        onClick={() => setTButtons(b => [...b, ""])}
                        className="shrink-0 text-xs text-[#1171ef] border border-[#1171ef] rounded-lg px-3 py-1.5 hover:bg-[#eef0fd] transition-colors flex items-center gap-1"
                      >
                        <i className="fas fa-plus text-[10px]" />{tl.addButton}
                      </button>
                    )}
                  </div>
                  {tButtons.length === 0 ? (
                    <p className="text-xs text-[#cad1d7] italic text-center py-1">{tl.noButtons}</p>
                  ) : (
                    <div className="space-y-2">
                      {tButtons.map((btn, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-[#1171ef] text-white text-[10px] flex items-center justify-center shrink-0 font-bold">{i + 1}</div>
                          <input
                            type="text"
                            value={btn}
                            maxLength={20}
                            onChange={e => setTButtons(b => b.map((v, j) => j === i ? e.target.value : v))}
                            className="flex-1 border border-[#cad1d7] rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef] bg-white"
                            placeholder={tl.buttonPlaceholder}
                          />
                          <span className="text-[10px] text-[#8898aa] shrink-0 w-8 text-right">{btn.length}/20</span>
                          <button
                            type="button"
                            onClick={() => setTButtons(b => b.filter((_, j) => j !== i))}
                            className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-[#cad1d7] hover:text-[#f5365c] hover:bg-red-50 transition-colors"
                          >
                            <i className="fas fa-times text-xs" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2 text-sm border border-[#e9ecef] rounded-lg text-[#525f7f]">{tc.cancel}</button>
                  <button
                    type="submit"
                    disabled={createTemplate.isPending || isUploading}
                    className="px-4 py-2 text-sm gradient-primary text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
                  >
                    {isUploading && <i className="fas fa-spinner fa-spin" />}
                    {createTemplate.isPending ? tc.creating : isUploading ? tl.uploading.replace("{pct}", String(progress)) : tl.createTemplate}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Category filter + search */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterCat === cat ? "gradient-primary text-white shadow-sm" : "bg-white border border-[#e9ecef] text-[#525f7f] hover:border-[#1171ef] hover:text-[#1171ef]"}`}
              >
                {tl.cats[cat as keyof typeof tl.cats] ?? cat}
              </button>
            ))}
          </div>
          <div className="relative">
            <i className="fas fa-search absolute start-3 top-1/2 -translate-y-1/2 text-[#adb5bd] text-xs pointer-events-none" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={tc.search}
              className="border border-[#e9ecef] rounded-lg ps-8 pe-3 py-1.5 text-xs text-[#32325d] focus:outline-none focus:ring-1 focus:ring-[#1171ef] w-48 bg-white"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <div key={i} className="h-44 bg-white rounded-xl border border-[#e9ecef] animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-12 text-center">
            <i className="fas fa-file-alt text-4xl text-[#cad1d7] mb-3 block" />
            <p className="text-[#8898aa] text-sm">{tl.noTemplatesFound}</p>
            <button onClick={() => { resetForm(); setShowForm(true); }} className="gradient-primary text-white px-5 py-2 rounded-lg text-sm mt-4 shadow-sm">
              <i className="fas fa-plus mr-2" />{tl.createTemplate}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(t => (
              <div key={t.id} className="bg-white rounded-xl shadow-sm border border-[#e9ecef] flex flex-col overflow-hidden">

                {/* Image preview */}
                {t.type === "image" && t.imageUrl && (
                  <div className="h-36 bg-[#f6f9fc] overflow-hidden">
                    <img
                      src={mediaUrl(t.imageUrl)}
                      alt={t.name}
                      className="w-full h-full object-cover"
                      onError={e => {
                        const el = e.target as HTMLImageElement;
                        el.parentElement!.innerHTML = `<div class="w-full h-full flex items-center justify-center text-[#cad1d7]"><i class="fas fa-image text-3xl"></i></div>`;
                      }}
                    />
                  </div>
                )}
                {t.type === "image" && !t.imageUrl && (
                  <div className="h-36 bg-[#f6f9fc] flex items-center justify-center text-[#cad1d7]">
                    <i className="fas fa-image text-3xl" />
                  </div>
                )}

                {/* Video preview */}
                {t.type === "video" && t.imageUrl && (
                  <div className="h-36 bg-black overflow-hidden relative group">
                    <video
                      src={mediaUrl(t.imageUrl)}
                      className="w-full h-full object-cover"
                      muted
                      playsInline
                      onMouseEnter={e => (e.target as HTMLVideoElement).play()}
                      onMouseLeave={e => { const v = e.target as HTMLVideoElement; v.pause(); v.currentTime = 0; }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity">
                      <div className="w-10 h-10 rounded-full bg-white/80 flex items-center justify-center">
                        <i className="fas fa-play text-[#1171ef] text-sm ml-0.5" />
                      </div>
                    </div>
                  </div>
                )}
                {t.type === "video" && !t.imageUrl && (
                  <div className="h-36 bg-[#f6f9fc] flex items-center justify-center text-[#cad1d7]">
                    <i className="fas fa-video text-3xl" />
                  </div>
                )}

                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h5 className="font-semibold text-[#32325d] text-sm">{t.name}</h5>
                        {t.type === "image" && (
                          <span className="text-[10px] bg-[#eef0fd] text-[#1171ef] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider">
                            <i className="fas fa-image mr-1" />IMG
                          </span>
                        )}
                        {t.type === "video" && (
                          <span className="text-[10px] bg-[#fce8f3] text-[#e83e8c] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider">
                            <i className="fas fa-video mr-1" />VID
                          </span>
                        )}
                      </div>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full mt-1 inline-block ${catColor(t.category ?? "")}`}>
                        {t.category}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => handleDuplicate(t.id)} className="text-[#cad1d7] hover:text-[#1171ef] text-sm" title={tl.duplicate}>
                        <i className="fas fa-copy" />
                      </button>
                      <button onClick={() => handleDelete(t.id)} className="text-[#cad1d7] hover:text-[#f5365c] text-sm" title={tc.delete}>
                        <i className="fas fa-trash" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-[#525f7f] flex-1 leading-relaxed line-clamp-3">{t.content}</p>
                  {(() => {
                    let btns: string[] = [];
                    try { btns = t.buttons ? JSON.parse(t.buttons) : []; } catch {}
                    return btns.length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {btns.map((b, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#eef0fd] text-[#1171ef] text-xs font-medium rounded-full border border-[#1171ef]/20">
                            <i className="fas fa-reply text-[9px]" />{b}
                          </span>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  <div className="mt-4">
                    <button
                      onClick={() => navigator.clipboard.writeText(t.content)}
                      className="w-full text-center text-xs font-medium py-1.5 border border-[#e9ecef] rounded-lg text-[#1171ef] hover:bg-[#eef0fd] transition-colors"
                    >
                      <i className="fas fa-copy mr-1" />{tl.copyText}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
